import { Separator } from '@base-ui/react/separator';
import styled from '@emotion/styled';
import { useUser } from '@libs/contexts';
import { ui } from '@libs/domain';
import { useItemName } from '@ps-smart-cp/ppfw-client';
import type { ReactNode } from 'react';
import { useState } from 'react';
import { useNavigate } from 'react-router';
import {
  CompanyButton,
  CompanyMenu,
  NotificationButton,
  UserButton,
  UserMenu,
} from '../global-area/components';
import { useNotification } from '../global-area/NotificationContext';
import { GradientBarV2 } from './GradientBarV2';
import { LogoV2 } from './LogoV2';
import { globalAreaV2Sizes } from './styles';

const Header = styled.header`
  display: flex;
  flex-direction: column;
  flex-shrink: 0;
  background-color: var(--ps-color-background-canvas);
  position: sticky;
  top: 0;
  z-index: var(--ps-layer-header);
`;

const LogoRow = styled.div`
  display: flex;
  align-items: center;
  gap: var(--ps-space-stack);
  padding-inline: var(--ps-space-inset-loose);
  height: ${globalAreaV2Sizes.rowHeight};
  flex-shrink: 0;
  box-sizing: border-box;
  border-bottom: var(--ps-border-width-divider) solid var(--ps-color-border-default);
`;

/** children の有無に関わらず右側の操作枠を右寄せにするスペーサー。 */
const Spacer = styled.div`
  flex: 1;
  min-width: 0;
`;

const Actions = styled.div`
  display: flex;
  align-items: center;
  gap: var(--ps-space-stack);
`;

const ActionSeparator = styled(Separator)`
  width: var(--ps-border-width-divider);
  height: 60%;
  background-color: var(--ps-color-border-default);
`;

export interface GlobalAreaV2Props {
  /** ロゴの右隣に置くサブシステム区分名（`FA` など）。 */
  subsystemName?: string;
  homePath?: string;
  /**
   * サブシステム内にいない場合に、会社切替のところに現在の会社領域ではなく
   * `会社を選択` を表示させる
   * @default true
   */
  isUnderSubsystem?: boolean;
  /** ロゴの右隣に並べる自由枠。処理月度ラベルなどページごとに変わる要素を差し込む。 */
  children?: ReactNode;
  /**
   * 通知ボタンを非表示にする。取得・保持はそのまま行い、表示だけを止める。
   * @default false
   */
  shouldHideNotification?: boolean;
}

export function GlobalAreaV2({
  subsystemName,
  homePath,
  isUnderSubsystem = true,
  children,
  shouldHideNotification = false,
}: GlobalAreaV2Props) {
  const { user } = useUser();
  const { notifications } = useNotification();
  const navigate = useNavigate();
  const getName = useItemName();
  const [companyMenuAnchorEl, setCompanyMenuAnchorEl] = useState<HTMLElement | null>(null);
  const [userMenuAnchorEl, setUserMenuAnchorEl] = useState<HTMLElement | null>(null);

  return (
    <Header role="banner" aria-label="global area">
      <LogoRow>
        <LogoV2
          brandLabel={subsystemName}
          onClick={homePath ? () => navigate(homePath) : undefined}
          title={homePath ? getName(ui.linkNames.toMainMenu) : undefined}
        />
        {children}
        <Spacer />
        {user && (
          <>
            <Actions>
              {!shouldHideNotification && (
                <>
                  <NotificationButton notifications={notifications} />
                  <ActionSeparator orientation="vertical" />
                </>
              )}
              <CompanyButton
                anchorEl={companyMenuAnchorEl}
                setAnchorEl={setCompanyMenuAnchorEl}
                user={user}
                isUnderSubsystem={isUnderSubsystem}
              />
              <ActionSeparator orientation="vertical" />
              <UserButton user={user} anchorEl={userMenuAnchorEl} setAnchorEl={setUserMenuAnchorEl} />
            </Actions>
            <CompanyMenu user={user} anchorEl={companyMenuAnchorEl} setAnchorEl={setCompanyMenuAnchorEl} />
            <UserMenu user={user} anchorEl={userMenuAnchorEl} setAnchorEl={setUserMenuAnchorEl} />
          </>
        )}
      </LogoRow>
      <GradientBarV2 />
    </Header>
  );
}
